import { useRef, useState } from 'react';

interface Props {
  currentUrl: string;
  purpose: string;
  /** Called with (s3Key, localPreviewUrl) after a successful upload. */
  onUpload: (key: string, previewUrl: string) => void;
  onRequestUpload: (input: {
    purpose: string;
    extension: string;
    contentType: string;
  }) => Promise<{ uploadUrl: string; key: string }>;
}

export function AvatarUpload({ currentUrl, purpose, onUpload, onRequestUpload }: Props): JSX.Element {
  const [preview, setPreview] = useState(currentUrl);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File) => {
    setUploading(true);
    setError('');
    const ext = (file.name.split('.').pop()?.toLowerCase() ?? 'jpg').slice(0, 5);
    const contentType = file.type || 'image/jpeg';
    try {
      const { uploadUrl, key } = await onRequestUpload({ purpose, extension: ext, contentType });
      if (uploadUrl) {
        await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': contentType } });
      }
      const localUrl = URL.createObjectURL(file);
      setPreview(localUrl);
      onUpload(key, localUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onUpload('', '');
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label className="bbm-label">Photo de profil</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 72, height: 72, borderRadius: 99,
            background: '#E8EFF5', overflow: 'hidden', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {preview ? (
            <img src={preview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ fontSize: 28, color: '#9FB0BF' }}>👤</span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            type="button"
            className="bbm-btn"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Envoi…' : preview ? 'Changer la photo' : 'Ajouter une photo'}
          </button>
          {preview && (
            <button type="button" className="bbm-btn bbm-btn--ghost" onClick={handleRemove}>
              Supprimer
            </button>
          )}
          {error && <span style={{ fontSize: 12, color: '#D9534F' }}>{error}</span>}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
